import { ColumnDef } from "@tanstack/react-table";
import { AxiosError, AxiosResponse } from "axios";
import React, { useEffect, useState, useRef } from "react";
import DataTable2 from "shared/DataTable2";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { DrugMedical, Result } from "../../interface/DrugMedicalGroup";
import { DrugMedicalGroupAxiosInstance } from "../../Network/DrugMedicalGroupAxiosInstance";
import { Option } from "../../interface/Option";
import FileUpload from "Pages/uploadFile/UploadFileDrugMedicalGroup/UploadFileDrugMedicalGroup";
import ListOfDrugCode from "../Shared/DrugCode";
import ListOfDrugMedical from "../Shared/DrugMedicalGroup";

const DrugMedicalGroup: React.FC = () => {
  // Main data state
  const [items, setItems] = useState<DrugMedical>({ result: [] });

  // Form states
  const [Name, setName] = useState<string>("");
  const [Description, setDescription] = useState<string>("");
  const [DrugCode, setDrugCode] = useState<Option[]>([]);
  const [medicalGroups, setMedicalGroups] = useState<Option[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);
  // UI control states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [clearDrugCode, setClearDrugCode] = useState(false);
  const [clearmedicalGroups, setClearMedicalGroups] = useState(false);
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceId, setForceId] = useState<number | string | null>(null);
  const [forceError, setForceError] = useState("");

  // Get token from localStorage
  const token = localStorage.getItem("token") || "";

  // dialog for details
  const [selectedItem, setSelectedItem] = useState<Result | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  // Initialize toast
  const { toast } = useToast();

  // Fetch initial data on component mount
  useEffect(() => {
    fetchItems();
  }, []);

  // Dialog details control handlers
  const handleOpenDialog = (item: Result) => {
    setSelectedItem(item);
    dialogRef.current?.showModal();
  };

  const handleCloseDialog = () => {
    dialogRef.current?.close();
    setSelectedItem(null);
  };
  const handleEditClick = (item: Result) => {
    handleEdit(item);
    setIsDialogOpen(true);
  };
  // Handlers for form inputs

  const handleDrugCodeChange = (selectedOptions: Option[]) => {
    console.log("selected:", selectedOptions);
    setDrugCode(selectedOptions);
  };
  const handleMedicalGroupsChange = (selectedOptions: Option[]) => {
    console.log("selected:", selectedOptions);
    setMedicalGroups(selectedOptions);
  };

  // Clear form function
  const clear = () => {
    setName("");
    setDescription("");
    setDrugCode([]);
    setMedicalGroups([]);
    setIsActive(true);
  };

  // Dialog control handlers
  const handleAddClick = () => {
    setEditIndex(null);
    clear();
    setIsDialogOpen(true);
  };

  // API Functions
  const fetchItems = async (): Promise<void> => {
    try {
      const response: AxiosResponse<{ success: boolean; result: Result[] }> =
        await DrugMedicalGroupAxiosInstance.get("getDrugsMedicalGroup");
      setItems({ result: [...response.data.result].sort((a: any, b: any) => b.id - a.id) });

      console.log(response.data.result);
    } catch (error) {
      console.error("Error fetching items:", error);
      toast({
        title: "Error",
        description: "Failed to fetch Drug Medical groups",
        variant: "destructive",
      });
    }
  };

  const addItem = async (): Promise<void> => {
    if (!Name.trim()) {
      toast({
        title: "Error",
        description: "Name is required and cannot be empty.",
        variant: "destructive",
      });
      return;
    }
    if (Name.trim().length < 3) {
      toast({
        title: "Error",
        description: " Name must be at least 3 characters long.",
        variant: "destructive",
      });
      return;
    }
    if (!DrugCode.length || DrugCode.every((option) => !option.value)) {
      toast({
        title: "Error",
        description: "At least one Drug code is required.",
        variant: "destructive",
      });
      return;
    }
    const ValuesDrugMedical = medicalGroups.map((option) => option.value);
    const ValuesDrugcode = DrugCode.map((option) => option.value);

    try {
      const newGroupArray = [
        {
          name: Name,
          description: Description,
          drugs: ValuesDrugcode,
          medical_groups: ValuesDrugMedical,
          is_active: isActive,
        },
      ];

      const payload = {
        data: newGroupArray,
        token,
      };
      console.log("newGroupArray:", newGroupArray);
      const response = await DrugMedicalGroupAxiosInstance.post(
        "addNewDrugsMedicalGroup",
        payload
      );
      if (response.data.success) {
        fetchItems();
        clear();
        setIsDialogOpen(false);

        toast({
          title: "Success",
          description: "New Drug Medical Group Added Successfully.",
        });
      } else {
        const errors = response.data.errors || [];
        const errorMessages = errors
          .map((error: any) => error.error)
          .join("\n");

        toast({
          title: "Error",
          description:
            errorMessages ||
            response.data.message ||
            "Failed to add the group.",
          variant: "destructive",
        });

        console.log(errors);
      }
    } catch (error: AxiosError | any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error || "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (item: Result) => {
    setName(item.name);
    // Convert text or numbers to Options
    const mapToOptions = (arr: (string | number)[]): Option[] =>
      arr.map((value) => ({
        value: value, // Actual value
        label: value.toString(), // The text that will appear in the Select
      }));
    setDrugCode(mapToOptions(item.drugs));

    //Convert or process medical_groups fields
    const MedicalGroupsData = Array.isArray(item.medical_groups)
      ? item.medical_groups.map((obj) =>
          typeof obj === "string" ? { id: 0, name: obj } : obj
        )
      : [];

    setMedicalGroups(
      MedicalGroupsData.map((obj) => ({
        value: obj.id,
        label: obj.name,
      }))
    );
    setIsActive(item.is_active);
    setDescription(item.description);
    setEditIndex(item.id);
  };

  const updateItem = async (id: string | number): Promise<void> => {
    if (!Name.trim()) {
      toast({
        title: "Error",
        description: "Name is required and cannot be empty.",
        variant: "destructive",
      });
      return;
    }
    if (Name.trim().length < 3) {
      toast({
        title: "Error",
        description: " Name must be at least 3 characters long.",
        variant: "destructive",
      });
      return;
    }
    if (!DrugCode.length || DrugCode.every((option) => !option.value)) {
      toast({
        title: "Error",
        description: "At least one Drug code is required.",
        variant: "destructive",
      });
      return;
    }
    const ValuesDrugMedical = medicalGroups.map((option) => option.value);
    const ValuesDrugcode = DrugCode.map((option) => option.value);

    try {
      const updatedItem = {
        data: {
          name: Name,
          description: Description,
          drugs: ValuesDrugcode,
          medical_groups: ValuesDrugMedical,
          is_active: isActive,
          id: id,
        },
        token: token,
      };
      console.log("updatedItem", updatedItem);
      const response = await DrugMedicalGroupAxiosInstance.put(
        `updateDrugsMedicalGroup`,
        updatedItem
      );
      if (response.data.success) {
        fetchItems();
        clear();
        setIsDialogOpen(false);
        toast({
          title: "Success",
          description: "Drug Medical group updated successfully.",
        });
      }
    } catch (error: AxiosError | any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error || "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const removeItem = async (id: string | number): Promise<void> => {
    try {
      await DrugMedicalGroupAxiosInstance.delete(
        `deleteDrugsMedicalGroup/${id}`,
        {
          data: { token: token },
        }
      );
      fetchItems();
      toast({
        title: "Success",
        description: "Drug Medical group Deleted Successfully.",
      });
    } catch (error) {
      console.error("Error removing item:", error);
      toast({
        title: "Error",
        description: "Failed to delete medical group",
        variant: "destructive",
      });
    }
  };

  const toggleActive = async (row: Result, force = false): Promise<void> => {
    try {
      const updatedItem = {
        data: {
          id: row.id,
          name: row.name,
          description: row.description || "",
          drugs: Array.isArray(row.drugs) ? row.drugs : [],
          medical_groups: Array.isArray(row.medical_groups) ? row.medical_groups.map((m: any) => m.id || m) : [],
          is_active: !row.is_active,
          force,
        },
        token,
      };

      const response = await DrugMedicalGroupAxiosInstance.put(
        `updateDrugsMedicalGroup`,
        updatedItem
      );

      if (response.data.success) {
        setItems((prev) => ({
          result: prev.result.map((i) =>
            i.id === row.id ? { ...i, is_active: !row.is_active } : i
          ),
        }));
        setForceDialogOpen(false);
        setForceId(null);
        setForceError("");
        toast({
          title: "Success",
          description: `Status updated to ${!row.is_active ? "Active" : "Inactive"}.`,
        });
      } else {
        setForceId(row.id);
        setForceError(response.data.error || "This group is currently used in predefined rule relations.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "";
      if (
        error.response?.status === 409 ||
        errMsg.toLowerCase().includes("used") ||
        errMsg.toLowerCase().includes("referenced") ||
        errMsg.toLowerCase().includes("cannot")
      ) {
        setForceId(row.id);
        setForceError(errMsg || "This group is currently used in predefined rule relations.");
        setForceDialogOpen(true);
      } else {
        toast({
          title: "Error",
          description: errMsg || "An unexpected error occurred.",
          variant: "destructive",
        });
      }
    }
  };

  // Table Columns Configuration
  const columns: ColumnDef<Result>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          ID
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
            ? " ↓"
            : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original; //Retrieve the full item
        return (
          <button onClick={() => handleOpenDialog(item)} className="id-button">
            {item.id}
          </button>
        );
      },
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Name
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
            ? " ↓"
            : ""}
        </Button>
      ),
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Description
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
            ? " ↓"
            : ""}
        </Button>
      ),
    },

    {
      accessorKey: "drugs",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Drug Code
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
            ? " ↓"
            : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const Drugs = row.getValue("drugs") as string[];
        return Array.isArray(Drugs) ? Drugs.join(", ") : "No Drugs";
      },
    },
    {
      accessorKey: "is_active",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Status
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
            ? " ↓"
            : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => toggleActive(item)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                item.is_active ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  item.is_active ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4">


      <h5
        className="mb-6 flex items-center gap-2"
        style={{ fontWeight: "bolder", fontSize: "22px", marginTop: "15px" }}
      >
        <span className="icon-container">
          <i className="fa-solid fa-pills"></i>
        </span>
        Drug Medical Group
      </h5>

      <FileUpload fetchItems={fetchItems} />
      <DataTable2
        columns={columns}
        data={items.result}
        searchColumns={["name"]}
        addButtonLabel="Add New Group"
        showAddButton={true}
        onEdit={handleEdit}
        onDelete={removeItem}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
      />
      {Boolean(selectedItem) && (
        <dialog ref={dialogRef} className="custom-dialog" open>
          {selectedItem && (
            <div className="dialog-content">
              {/* top-dialog-close-icon */}
              <button className="dialog-close-icon" onClick={handleCloseDialog}>
                ×
              </button>
              {/* dialog-title */}
              <h2 className="dialog-title">
                <span>Details for ID:</span> {selectedItem.id}
              </h2>
              {/* Content */}
              <div className="dialog-grid">
                <div className="dialog-row two-col-row">
                  <div className="dialog-item">
                    <strong>Name:</strong>
                    <p>{selectedItem.name}</p>
                  </div>
                  <div className="dialog-item">
                    <strong> Description:</strong>
                    <p>{selectedItem.description}</p>
                  </div>
                </div>
                <div className="dialog-row three-col-row">
                  <div className="dialog-item">
                    <strong>Drug Code:</strong>
                    <p>{selectedItem.drugs.join(", ")}</p>
                  </div>
                  <div className="dialog-item">
                    <strong>Drug Medical Groups:</strong>
                    <p>
                      {selectedItem.medical_groups
                        .map((n) => n.name)
                        .join(", ")}
                    </p>
                  </div>
                  <div className="dialog-item">
                    <strong>Is Active:</strong>{" "}
                    <span>{String(selectedItem.is_active)}</span>
                  </div>
                </div>
              </div>
              <button className="dialog-close-btn" onClick={handleCloseDialog}>
                Close
              </button>
            </div>
          )}
        </dialog>
      )}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editIndex === null
                ? "Add Drug Medical Group"
                : "Edit Drug Medical Group"}
            </DialogTitle>
            </DialogHeader>

          <div className="grid grid-cols-2 gap-4 mt-4">
            {/* First Column */}
            <div>
              <div className="mb-4">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={Name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter group name"
                />
              </div>
              <div className="mb-4">
                <Label htmlFor="drug_code"> Drug Code:</Label>
                <ListOfDrugCode
                  onChange={handleDrugCodeChange}
                  defaultValue={DrugCode}
                  clear={clearDrugCode}
                />
              </div>
            </div>
            {/* Second Column */}
            <div>
              <div className="mb-4">
                <Label htmlFor="description">Description:</Label>
                <Input
                  id="description"
                  value={Description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description"
                />
              </div>
              <div>
                <Label htmlFor="Drug_medicalgroups">
                  {" "}
                  Drug Medical Groups:
                </Label>
                <ListOfDrugMedical
                  onChange={handleMedicalGroupsChange}
                  defaultValue={medicalGroups}
                  clear={clearmedicalGroups}
                />
              </div>
              <div className="mb-3 w-80">
                <Accordion type="single" collapsible>
                  <AccordionItem value="item-1">
                    <AccordionTrigger>Is Active:</AccordionTrigger>
                    <AccordionContent>
                      <div>
                        <label>
                          <input
                            type="radio"
                            name="is_active"
                            value="true"
                            checked={isActive === true}
                            onChange={(e) =>
                              setIsActive(e.target.value === "true")
                            }
                          />{" "}
                          True
                        </label>
                        <label className="ml-4">
                          <input
                            type="radio"
                            name="is_active"
                            value="false"
                            checked={isActive === false}
                            onChange={(e) =>
                              setIsActive(e.target.value === "true")
                            }
                          />{" "}
                          False
                        </label>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 m-2">
            <Button
              onClick={() => {
                if (editIndex === null) {
                  addItem();
                } else {
                  updateItem(editIndex);
                }
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editIndex === null ? "Add Relation " : "Update Relation "}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                clear();
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "28px 24px", maxWidth: 440, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", textAlign: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                ⚠️ Warning
              </h3>
              <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <i className="fa-solid fa-triangle-exclamation" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                  <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6, fontWeight: 600 }}>
                    ⚠️ “Warning: This group is currently used in predefined rule relations. Deactivating it will disable all dependent relations.”
                  </p>
                </div>
              </div>
              {forceError && (
                <p style={{ fontSize: 12, color: "#991b1b", margin: 0, fontStyle: "italic" }}>
                  {forceError}
                </p>
              )}
              <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                Do you want to <strong style={{ color: "#dc2626" }}>deactivate</strong> anyway?
              </p>
              <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                <button
                  onClick={() => {
                    setForceDialogOpen(false);
                    setForceId(null);
                    setForceError("");
                  }}
                  style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!forceId) return;
                    const item = items.result.find((i) => i.id === forceId);
                    if (item) toggleActive(item, true);
                  }}
                  style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}
                >
                  <i className="fa-solid fa-bolt" style={{ marginRight: 8 }}></i>Deactivate Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default DrugMedicalGroup;
